import { update } from 'state/util';
import { STATUS } from 'state/requestStatus/reducer';

function makeKey(pageId, userId) {
  return `${pageId}#${userId}`;
}

/**
 * Storage for personal vote preferences & relaying status messages
 * related to the current operation.
 *
 * votePrefs state sub-tree:
 * {
 *   "candidate-page-id#userId": {
 *     data: {
 *       vote: "Y", // Y || N, null or missing means undecided
 *       reason: "This is the full reasoning in markdown",
 *       shared: true, // boolean, any non-true value means private
 *     },
 *     message: null || "some status/error message",
 *     status: STATUS.COMPLETE
 *   },
 *   ... other page preferences ...,
 * }
 *
 * Action Dispatch Structure:
 *  {
 *    type: "[WRITE|GET]_VOTE_PREF_REQUESTED",
 *    payload: {
 *      pageId: "example-candidate-page",
 *      userId: "my userId",
 *      data: {
 *        vote: "Y",
 *        reason: "This is the full reasoning in markdown",
 *        shared: true,
 *      }
 *    }
 *  }
 */

// we add page keys to this as we add preferences
const initialState = {};

export const votePrefsReducer = (state, action) => {
  if (state === undefined)
    return initialState;

  if (action.type === "LOGOUT_REQUESTED")
      return initialState;

  else if (action.type === "BULK_VOTE_PREFS_SUCCESSFUL") {
    const votePrefs = {};
    action.payload.forEach(pref => {
      const pageId = pref.pageId;
      const userId = pref.userId;
      const key = makeKey(pageId, userId);
      votePrefs[key] = pref;
    });
    return update(state, votePrefs);
  }

  let key = null;
  let currentData = null;
  if (action.payload && action.payload.pageId && action.payload.userId) {
    key = makeKey(action.payload.pageId, action.payload.userId);
    currentData = state[key] ? state[key].data : {};
  }

  switch(action.type) {
    // using "WRITE" because create/update are same
    case "WRITE_VOTE_PREF_REQUESTED":
      return update(state, {
        [key]: {
          message: "Saving...",
          status: STATUS.SAVING,
        },
      });

    case "WRITE_VOTE_PREF_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: action.payload.data,
          user: action.payload.user,
          message: "Save successful",
          status: STATUS.COMPLETE,
        },
      });

    case "WRITE_VOTE_PREF_FAILED":
      return update(state, {
        [key]: {
          message: action.payload.message,
          status: STATUS.FAILED,
        },
      });

    case "GET_VOTE_PREF_REQUESTED":
      return update(state, {
        [key]: {
          data: currentData || {},
          message: "Loading page...",
          status: STATUS.LOADING,
        },
      });

    case "GET_VOTE_PREF_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: action.payload.data,
          user: action.payload.user,
          message: null,
          status: STATUS.COMPLETE,
        },
      });

    case "GET_VOTE_PREF_FAILED":
      const errPageId = action.payload.data.pageId;
      const errUserId = action.payload.data.userId;
      if (action.payload.statusCode === 404) {
        // return a blank vote pref for creating if not found
        return update(state, {
          [`${errPageId}#${errUserId}`]: {
            message: null,
            data: {},
            status: STATUS.NOT_FOUND,
          },
        });
      }
      return update(state, {
        [`${errPageId}#${errUserId}`]: {
          message: action.payload.message,
          status: STATUS.FAILED,
        },
      });

    default:
      return state;
  }
};




// WEBPACK FOOTER //
// ./src/state/votePrefs/reducer.js